import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "download-tip" }
const _hoisted_2 = { class: "count" }

import { reactive, ref } from "vue";
import { message } from "ant-design-vue";
import { postWrongQuestionDownload } from "@/servers/api/wrongQuestion";

type WrongQuestionRecord = Record<string, any>;


export default /*@__PURE__*/_defineComponent({
  __name: 'WrongQuestionDownloadModal',
  props: {
    open: { type: Boolean },
    studentId: {},
    total: {},
    questionList: {},
    queryParams: {}
  },
  emits: ["update:open", "download-success"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const downloading = ref(false);

const form = reactive({
  includes: ["questionsId", "answer", "analysis"] as string[],
});

function numberId(value: unknown): number | undefined {
  const num = Number(String(value ?? "").trim());
  return Number.isInteger(num) && num > 0 ? num : undefined;
}

function extractQuestionIds(list: WrongQuestionRecord[]) {
  const ids = list
    .map((item) => numberId(item.question_id ?? item.test_question_id))
    .filter((id): id is number => id !== undefined);
  return Array.from(new Set(ids));
}

function getResponseHeader(response: any, name: string) {
  return response?.headers?.get?.(name) || response?.headers?.[name] || response?.headers?.[name.toLowerCase()] || "";
}

function decodeFilename(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function filenameFromDisposition(disposition: string) {
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeFilename(utf8Match[1].replace(/["']/g, ""));
  const normalMatch = disposition.match(/filename="?([^"]+)"?/i);
  return normalMatch?.[1] ? decodeFilename(normalMatch[1]) : "";
}

function extensionFromType(type: string) {
  if (type.includes("wordprocessingml.document")) return "docx";
  if (type.includes("pdf")) return "pdf";
  if (type.includes("zip")) return "zip";
  return "pdf";
}

function downloadBlob(blob: Blob, filename?: string) {
  const date = new Date().toLocaleDateString("zh-CN").replace(/\//g, "");
  const safeFilename = filename || `错题_${date}.${extensionFromType(blob.type)}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = safeFilename;
  a.click();
  URL.revokeObjectURL(url);
}

async function readBackendError(blob: Blob) {
  if (!blob.type.includes("json") && !blob.type.includes("text")) return "";
  try {
    const data = JSON.parse(await blob.text());
    return data?.msg || data?.message || data?.detail || "";
  } catch {
    return "";
  }
}

async function resolveDownloadError(error: any) {
  const data = error?.data;
  if (data instanceof Blob) {
    const backendMessage = await readBackendError(data);
    if (backendMessage) return backendMessage;
  }
  if (typeof data === "string") return data;
  if (data && typeof data === "object") return data.msg || data.message || data.detail || "";

  const response = error?.response;
  if (response?.clone) {
    try {
      const contentType = response.headers?.get?.("content-type") || "";
      if (contentType.includes("json")) {
        const json = await response.clone().json();
        return json?.msg || json?.message || json?.detail || "";
      }
      const responseText = await response.clone().text();
      if (responseText) return responseText;
    } catch {}
  }

  return error?.message || "";
}

async function handleDownload() {
  if (!props.total) {
    message.warning("当前无题目可下载，请先选择题目");
    return;
  }

  const list = props.questionList ?? [];
  if (!list.length) {
    message.warning("题目数据未加载，请先选择题目");
    return;
  }

  const questionIds = extractQuestionIds(list);
  if (!questionIds.length) {
    message.warning("当前题目缺少 question_id，无法调用后端下载接口");
    return;
  }
  if (questionIds.length < list.length) {
    message.warning(`有 ${list.length - questionIds.length} 道题缺少 question_id，本次只下载可识别的题目`);
  }

  downloading.value = true;
  try {
    const studentId = String(props.studentId ?? "").trim();
    const payload: API.WrongQuestionDownload = {
      question_ids: questionIds,
      is_need_answer: form.includes.includes("answer"),
      is_need_analysis: form.includes.includes("analysis"),
    };
    if (studentId) payload.student_id = studentId;

    const result: any = await postWrongQuestionDownload(
      payload,
      { responseType: "blob", getResponse: true }
    );
    const response: Response | undefined = result?.response;
    let blob: Blob;

    if (result?.data instanceof Blob) {
      blob = result.data;
    } else if (response?.blob) {
      blob = await response.blob();
    } else if (result instanceof Blob) {
      blob = result;
    } else {
      message.error("下载响应异常，无法获取文件数据");
      return;
    }

    const backendError = await readBackendError(blob);
    if (backendError) throw new Error(backendError);

    const filename = filenameFromDisposition(getResponseHeader(response, "content-disposition"));
    downloadBlob(blob, filename);
    message.success("下载成功");
    emit("download-success", questionIds);
    emit("update:open", false);
  } catch (error) {
    console.error("错题下载失败:", error);
    const errorText = await resolveDownloadError(error);
    message.error(errorText || "下载失败，请重试");
  } finally {
    downloading.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_a_checkbox = _resolveComponent("a-checkbox")!
  const _component_a_col = _resolveComponent("a-col")!
  const _component_a_row = _resolveComponent("a-row")!
  const _component_a_checkbox_group = _resolveComponent("a-checkbox-group")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_form = _resolveComponent("a-form")!
  const _component_a_divider = _resolveComponent("a-divider")!
  const _component_a_modal = _resolveComponent("a-modal")!

  return (_openBlock(), _createBlock(_component_a_modal, {
    open: __props.open,
    title: "错题下载",
    width: "480px",
    "confirm-loading": downloading.value,
    "ok-text": "立即下载",
    "cancel-text": "取消",
    onOk: handleDownload,
    onCancel: _cache[1] || (_cache[1] = ($event: any) => (emit('update:open', false)))
  }, {
    default: _withCtx(() => [
      _createVNode(_component_a_form, {
        "label-col": { span: 5 },
        "wrapper-col": { span: 19 },
        style: {"margin-top":"8px"}
      }, {
        default: _withCtx(() => [
          _createVNode(_component_a_form_item, { label: "包含内容" }, {
            default: _withCtx(() => [
              _createVNode(_component_a_checkbox_group, {
                value: form.includes,
                "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((form.includes) = $event))
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_a_row, { gutter: [0, 8] }, {
                    default: _withCtx(() => [
                      _createVNode(_component_a_col, { span: 8 }, {
                        default: _withCtx(() => [
                          _createVNode(_component_a_checkbox, { value: "questionsId" }, {
                            default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                              _createTextVNode("题目", -1)
                            ]))]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_a_col, { span: 8 }, {
                        default: _withCtx(() => [
                          _createVNode(_component_a_checkbox, { value: "answer" }, {
                            default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                              _createTextVNode("答案", -1)
                            ]))]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_a_col, { span: 8 }, {
                        default: _withCtx(() => [
                          _createVNode(_component_a_checkbox, { value: "analysis" }, {
                            default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                              _createTextVNode("解析", -1)
                            ]))]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["value"])
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_a_divider, { style: {"margin":"12px 0"} }),
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("span", null, [
          _cache[5] || (_cache[5] = _createTextVNode("共 ", -1)),
          _createElementVNode("span", _hoisted_2, _toDisplayString(__props.total), 1),
          _cache[6] || (_cache[6] = _createTextVNode(" 题将被下载", -1))
        ])
      ])
    ]),
    _: 1
  }, 8, ["open", "confirm-loading"]))
}
}

})